import type { Role } from '@pirouette/shared';

// Guided "What do you want to do?" command-bar intent catalog.
//
// Deterministic, NO LLM. Each Intent maps a plain-language goal to a REAL
// app route (verified against apps/web/src/routes/*). The command bar fuzzy-
// matches the user's phrase against an intent's label + synonyms, filters by
// the viewer's role, and navigates on selection.
//
// Routing is NOT a security boundary here — RLS and the per-role route
// `beforeLoad` guards are. `requiredRole` only decides which intents a given
// dashboard surfaces, mirroring the role each route already lives under.

export type IntentCategory =
  | 'navigate'
  | 'people'
  | 'classes'
  | 'billing'
  | 'productions'
  | 'competitions'
  | 'communication'
  | 'reports'
  | 'settings'
  | 'video';

export interface Intent {
  /** Stable id — used for recents/suggested persistence and React keys. */
  id: string;
  /** Plain-language label shown as the primary result text. */
  label: string;
  /** One-line clarification of what selecting the intent does. */
  description: string;
  /** Real TanStack route path. Dynamic segments are intentionally avoided. */
  route: string;
  /** Alternate phrasings the user might type. Drives fuzzy matching. */
  synonyms: string[];
  category: IntentCategory;
  /**
   * Roles allowed to see this intent. Omit for "everyone". A viewer sees an
   * intent only when their role is listed (or the list is absent). This
   * mirrors the role each route already lives under — it is a filter, not a
   * security control.
   */
  requiredRole?: Role[];
  /**
   * Optional capability hints (advisory). Reserved for future fine-grained
   * gating; the command bar treats these as metadata only today.
   */
  requiredPermissions?: string[];
  /**
   * Optional query param the destination route can read to open a modal /
   * sub-view (e.g. `{ key: 'new', value: '1' }`). Only set when the target
   * route trivially supports it. Navigation always still routes by path.
   */
  openModal?: { key: string; value: string };
  /** Links this intent to a multi-step guided recipe in recipes.ts. */
  recipeId?: string;
}

// ---------------------------------------------------------------------------
// Intent catalog. Grouped by audience for readability; order here also seeds
// the default "suggested" list shown before the user types.
// ---------------------------------------------------------------------------

const ADMIN_INTENTS: Intent[] = [
  {
    id: 'admin-overview',
    label: 'Go to the admin dashboard',
    description: 'Open your studio overview.',
    route: '/admin',
    synonyms: ['home', 'dashboard', 'overview', 'admin home', 'main screen'],
    category: 'navigate',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-families',
    label: 'View families',
    description: 'Browse and search your family roster.',
    route: '/admin/families',
    synonyms: ['parents', 'guardians', 'households', 'family list', 'see families'],
    category: 'people',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-family-new',
    label: 'Add a new family',
    description: 'Create a family record by hand.',
    route: '/admin/families/new',
    synonyms: ['new family', 'create family', 'register family', 'enroll a family', 'add parent'],
    category: 'people',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-families-import',
    label: 'Import my roster',
    description: 'Bulk-import families from a CSV file.',
    route: '/admin/families/import',
    synonyms: ['import families', 'upload roster', 'csv import', 'bulk add families', 'import csv'],
    category: 'people',
    requiredRole: ['admin'],
    recipeId: 'import-roster',
  },
  {
    id: 'admin-dancers',
    label: 'View dancers',
    description: 'Browse the dancer roster.',
    route: '/admin/dancers',
    synonyms: ['students', 'kids', 'dancer list', 'see dancers', 'roster'],
    category: 'people',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-dancers-import',
    label: 'Import dancers',
    description: 'Bulk-import dancers from a CSV file.',
    route: '/admin/dancers/import',
    synonyms: ['upload dancers', 'csv dancers', 'bulk add dancers', 'import students'],
    category: 'people',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-classes',
    label: 'View classes',
    description: 'Browse the class schedule and rosters.',
    route: '/admin/classes',
    synonyms: ['lessons', 'sessions', 'class list', 'see classes', 'schedule'],
    category: 'classes',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-class-new',
    label: 'Create a new class',
    description: 'Add a class to the schedule.',
    route: '/admin/classes/new',
    synonyms: ['new class', 'add class', 'make a class', 'create lesson', 'set up a class'],
    category: 'classes',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-programs',
    label: 'View programs and seasons',
    description: 'Manage programs, sessions, and seasons.',
    route: '/admin/programs',
    synonyms: ['seasons', 'terms', 'sessions', 'program list', 'see programs'],
    category: 'classes',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-program-new',
    label: 'Create a new season',
    description: 'Set up a new program or season.',
    route: '/admin/programs/new',
    synonyms: ['new season', 'new program', 'add term', 'start a season', 'create program'],
    category: 'classes',
    requiredRole: ['admin'],
    recipeId: 'create-season',
  },
  {
    id: 'admin-assessments',
    label: 'View assessments',
    description: 'Review dancer assessments and progress.',
    route: '/admin/assessments',
    synonyms: ['evaluations', 'progress reviews', 'skill checks', 'gradings'],
    category: 'classes',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-leads',
    label: 'View leads and trials',
    description: 'Track prospective families and trial requests.',
    route: '/admin/leads',
    synonyms: ['prospects', 'trials', 'inquiries', 'waitlist', 'sign-ups', 'trial requests'],
    category: 'people',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-convert-trial',
    label: 'Convert a trial to enrollment',
    description: 'Turn a lead or trial into an enrolled family.',
    route: '/admin/leads',
    synonyms: ['enroll a trial', 'convert lead', 'sign up a prospect', 'trial to enrollment'],
    category: 'people',
    requiredRole: ['admin'],
    recipeId: 'convert-trial',
  },
  {
    id: 'admin-reports',
    label: 'See revenue and reports',
    description: 'Open studio reports and financial summaries.',
    route: '/admin/reports',
    synonyms: ['revenue', 'analytics', 'metrics', 'financials', 'income', 'stats', 'numbers'],
    category: 'reports',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-messages',
    label: 'Read and send messages',
    description: 'Open studio message threads with families.',
    route: '/admin/messages',
    synonyms: ['inbox', 'chat', 'conversations', 'dm families', 'message a parent'],
    category: 'communication',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-broadcasts',
    label: 'Text families',
    description: 'Send a broadcast announcement to families.',
    route: '/admin/broadcasts',
    synonyms: [
      'broadcast',
      'announcement',
      'send a text',
      'sms families',
      'mass message',
      'notify families',
      'email families',
    ],
    category: 'communication',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-setup-sms',
    label: 'Set up SMS texting',
    description: 'Configure text-message announcements to families.',
    route: '/admin/settings/sms',
    synonyms: ['enable sms', 'configure texting', 'turn on texts', 'set up texting'],
    category: 'communication',
    requiredRole: ['admin'],
    recipeId: 'setup-sms',
  },
  {
    id: 'admin-productions',
    label: 'View recitals and productions',
    description: 'Manage recitals, shows, and lineups.',
    route: '/admin/productions',
    synonyms: ['recitals', 'shows', 'performances', 'concert', 'production list'],
    category: 'productions',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-production-new',
    label: 'Make a recital',
    description: 'Create a new recital or production.',
    route: '/admin/productions/new',
    synonyms: [
      'new recital',
      'new production',
      'create a show',
      'set up a recital',
      'set up my recital',
      'add a performance',
      'plan a recital',
    ],
    category: 'productions',
    requiredRole: ['admin'],
    recipeId: 'setup-recital',
  },
  {
    id: 'admin-recital-packages',
    label: 'Manage recital packages',
    description: 'Configure recital fee packages and add-ons.',
    route: '/admin/recital-packages',
    synonyms: ['recital fees', 'show packages', 'costume packages', 'recital pricing'],
    category: 'productions',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-order-dances',
    label: 'Order dances in the lineup',
    description: 'Open productions to arrange the show running order.',
    route: '/admin/productions',
    synonyms: ['reorder dances', 'running order', 'lineup order', 'sequence dances', 'show order'],
    category: 'productions',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-competitions',
    label: 'View competitions',
    description: 'Manage competitions, routines, and entries.',
    route: '/admin/competitions',
    synonyms: ['comps', 'competition list', 'routines', 'see competitions'],
    category: 'competitions',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-competition-new',
    label: 'Add a competition',
    description: 'Create a new competition event.',
    route: '/admin/competitions/new',
    synonyms: ['new competition', 'create comp', 'add comp', 'set up a competition'],
    category: 'competitions',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-costume-measurements',
    label: 'Add costume measurements',
    description: 'Record costume sizes against dancer profiles.',
    route: '/admin/dancers',
    synonyms: [
      'costume sizes',
      'measurements',
      'add costume sizes',
      'costume fitting',
      'sizing',
      'dancer measurements',
    ],
    category: 'people',
    requiredRole: ['admin'],
    recipeId: 'costume-measurements',
  },
  {
    id: 'admin-billing',
    label: 'Open billing',
    description: 'View family balances, charges, and payments.',
    route: '/admin/billing',
    synonyms: ['invoices', 'payments', 'charges', 'accounts', 'money', 'tuition'],
    category: 'billing',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-billing-new',
    label: 'Charge a family',
    description: 'Record a new charge against a family account.',
    route: '/admin/billing/new',
    synonyms: [
      'charge a parent',
      'add a charge',
      'bill a family',
      'create invoice',
      'new charge',
      'record a payment',
      'collect tuition',
    ],
    category: 'billing',
    requiredRole: ['admin'],
    recipeId: 'charge-family',
  },
  {
    id: 'admin-billing-export',
    label: 'Export to QuickBooks',
    description: 'Download a CSV of charges for accounting.',
    route: '/admin/billing/export',
    synonyms: [
      'quickbooks',
      'export billing',
      'accounting export',
      'export charges',
      'download ledger',
      'csv export',
      'export to quickbooks',
    ],
    category: 'billing',
    requiredRole: ['admin'],
    recipeId: 'export-quickbooks',
  },
  {
    id: 'admin-billing-late-fees',
    label: 'Apply late fees',
    description: 'Review and assess late fees on overdue balances.',
    route: '/admin/billing/late-fees',
    synonyms: ['late fees', 'overdue charges', 'penalties', 'past due'],
    category: 'billing',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-billing-plans',
    label: 'Manage payment plans',
    description: 'Configure tuition and billing plans.',
    route: '/admin/billing/plans',
    synonyms: ['tuition plans', 'pricing plans', 'fee schedules', 'billing plans'],
    category: 'billing',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-billing-subscriptions',
    label: 'Manage subscriptions',
    description: 'View recurring tuition subscriptions.',
    route: '/admin/billing/subscriptions',
    synonyms: ['recurring billing', 'auto billing', 'subscription list', 'recurring tuition'],
    category: 'billing',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-video',
    label: 'Manage class video',
    description: 'Open the studio video library and storage.',
    route: '/admin/storage',
    synonyms: ['videos', 'recordings', 'media library', 'class footage', 'storage'],
    category: 'video',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-security',
    label: 'Set up two-factor security',
    description: 'Configure admin email MFA on your account.',
    route: '/admin/security',
    synonyms: ['2fa', 'mfa', 'two factor', 'secure my account', 'login security'],
    category: 'settings',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-audit',
    label: 'View the audit log',
    description: 'Review the security and activity audit trail.',
    route: '/admin/audit',
    synonyms: ['activity log', 'history', 'security log', 'audit trail', 'who did what'],
    category: 'settings',
    requiredRole: ['admin'],
  },
  {
    id: 'admin-cost',
    label: 'View cost and usage',
    description: 'See platform cost and usage details.',
    route: '/admin/cost',
    synonyms: ['usage', 'spend', 'platform cost', 'billing usage'],
    category: 'reports',
    requiredRole: ['admin'],
  },
];

const FACULTY_INTENTS: Intent[] = [
  {
    id: 'faculty-today',
    label: 'Go to my teaching day',
    description: "Open today's faculty dashboard.",
    route: '/faculty',
    synonyms: ['faculty home', 'my day', 'teacher dashboard', 'today', 'overview'],
    category: 'navigate',
    requiredRole: ['faculty', 'admin'],
  },
  {
    id: 'faculty-classes',
    label: 'View my classes',
    description: 'See the classes you teach.',
    route: '/faculty/classes',
    synonyms: ['my lessons', 'assigned classes', 'class list', 'my schedule'],
    category: 'classes',
    requiredRole: ['faculty', 'admin'],
  },
  {
    id: 'faculty-take-attendance',
    label: 'Take attendance',
    description: 'Open your classes to mark who showed up.',
    route: '/faculty/classes',
    synonyms: [
      'attendance',
      'mark attendance',
      'check in students',
      'roll call',
      'record attendance',
      'who is here',
    ],
    category: 'classes',
    requiredRole: ['faculty', 'admin'],
    recipeId: 'take-attendance',
  },
  {
    id: 'faculty-productions',
    label: 'View my productions',
    description: 'See recitals and shows for your classes.',
    route: '/faculty/productions',
    synonyms: ['my recitals', 'my shows', 'faculty productions', 'performances'],
    category: 'productions',
    requiredRole: ['faculty', 'admin'],
  },
];

const PARENT_INTENTS: Intent[] = [
  {
    id: 'parent-today',
    label: "Go to today's overview",
    description: "See today's schedule and updates.",
    route: '/parent',
    synonyms: ['home', 'parent home', 'today', 'dashboard', 'overview'],
    category: 'navigate',
    requiredRole: ['parent'],
  },
  {
    id: 'parent-dancers',
    label: 'View my dancers',
    description: "See your enrolled dancers' details.",
    route: '/parent/dancers',
    synonyms: ['my kids', 'my children', 'my students', 'dancer profiles'],
    category: 'people',
    requiredRole: ['parent'],
  },
  {
    id: 'parent-progress',
    label: "See my dancer's progress",
    description: 'Review assessments and progress reports.',
    route: '/parent/progress',
    synonyms: ['progress', 'evaluations', 'report card', 'skill progress', 'assessments'],
    category: 'reports',
    requiredRole: ['parent'],
  },
  {
    id: 'parent-schedule',
    label: 'View the class schedule',
    description: "See your dancers' upcoming classes.",
    route: '/parent/schedule',
    synonyms: ['schedule', 'class times', 'calendar', 'upcoming classes', 'when is class'],
    category: 'classes',
    requiredRole: ['parent'],
  },
  {
    id: 'parent-productions',
    label: 'View recitals and shows',
    description: 'See recital info and ticketing.',
    route: '/parent/productions',
    synonyms: ['recitals', 'shows', 'performances', 'concert', 'recital info'],
    category: 'productions',
    requiredRole: ['parent'],
  },
  {
    id: 'parent-competitions',
    label: 'View competitions',
    description: "See your dancers' competition schedule.",
    route: '/parent/competitions',
    synonyms: ['comps', 'competition schedule', 'routines', 'competition info'],
    category: 'competitions',
    requiredRole: ['parent'],
  },
  {
    id: 'parent-messages',
    label: 'Message the studio',
    description: 'Open your message threads with the studio.',
    route: '/parent/messages',
    synonyms: ['inbox', 'chat', 'contact studio', 'message', 'talk to the studio'],
    category: 'communication',
    requiredRole: ['parent'],
  },
  {
    id: 'parent-billing',
    label: 'View my bill and pay',
    description: 'See your balance and make a payment.',
    route: '/parent/billing',
    synonyms: ['pay', 'invoices', 'my balance', 'tuition', 'make a payment', 'what i owe'],
    category: 'billing',
    requiredRole: ['parent'],
  },
  {
    id: 'parent-auto-pay',
    label: 'Set up auto-pay',
    description: 'Turn on automatic tuition payments.',
    route: '/parent/billing/auto-pay',
    synonyms: ['autopay', 'automatic payments', 'recurring payment', 'auto billing'],
    category: 'billing',
    requiredRole: ['parent'],
  },
  {
    id: 'parent-payment-methods',
    label: 'Manage payment methods',
    description: 'Add or update your saved cards.',
    route: '/parent/billing/payment-methods',
    synonyms: ['payment methods', 'add a card', 'update card', 'my cards', 'credit card'],
    category: 'billing',
    requiredRole: ['parent'],
  },
  {
    id: 'parent-passes',
    label: 'View my passes',
    description: 'See class passes and punch cards.',
    route: '/parent/passes',
    synonyms: ['passes', 'punch card', 'class credits', 'drop-in passes'],
    category: 'classes',
    requiredRole: ['parent'],
  },
  {
    id: 'parent-notifications',
    label: 'Notification settings',
    description: 'Choose how the studio contacts you.',
    route: '/parent/settings/notifications',
    synonyms: ['settings', 'notifications', 'alerts', 'email preferences', 'text preferences'],
    category: 'settings',
    requiredRole: ['parent'],
  },
  {
    id: 'parent-account',
    label: 'Account settings',
    description: 'Manage or delete your account.',
    route: '/parent/settings/account',
    synonyms: ['account', 'delete account', 'close account', 'delete my account', 'remove account'],
    category: 'settings',
    requiredRole: ['parent'],
  },
];

const DANCER_INTENTS: Intent[] = [
  {
    id: 'dancer-schedule',
    label: 'View my schedule',
    description: 'See your upcoming classes.',
    route: '/dancer',
    synonyms: ['schedule', 'my classes', 'class times', 'today', 'calendar'],
    category: 'classes',
    requiredRole: ['dancer'],
  },
  {
    id: 'dancer-competitions',
    label: 'View my competitions',
    description: 'See your competition schedule and routines.',
    route: '/dancer/competitions',
    synonyms: ['comps', 'my routines', 'competition schedule', 'competitions'],
    category: 'competitions',
    requiredRole: ['dancer'],
  },
  {
    id: 'dancer-videos',
    label: 'Watch my videos',
    description: 'Open your class video library.',
    route: '/dancer/videos',
    synonyms: ['videos', 'recordings', 'my footage', 'watch class', 'media'],
    category: 'video',
    requiredRole: ['dancer'],
  },
];

/** The full deterministic intent catalog. */
export const INTENTS: Intent[] = [
  ...ADMIN_INTENTS,
  ...FACULTY_INTENTS,
  ...PARENT_INTENTS,
  ...DANCER_INTENTS,
];

// ---------------------------------------------------------------------------
// Role filtering
// ---------------------------------------------------------------------------

/**
 * Whether `role` may see `intent`. An intent with no `requiredRole` is visible
 * to everyone; otherwise the role must be in the list. A null/undefined role
 * (signed-out or unknown) only sees the "everyone" intents.
 */
export function intentVisibleToRole(intent: Intent, role: Role | null | undefined): boolean {
  if (!intent.requiredRole || intent.requiredRole.length === 0) return true;
  if (!role) return false;
  return intent.requiredRole.includes(role);
}

/** All intents the given role is allowed to see, preserving catalog order. */
export function intentsForRole(role: Role | null | undefined): Intent[] {
  return INTENTS.filter((intent) => intentVisibleToRole(intent, role));
}

// ---------------------------------------------------------------------------
// Fuzzy matching — small, dependency-free.
//
// Scoring favors, in order: exact label/synonym equality, prefix matches,
// whole-substring matches, then subsequence (characters appearing in order).
// Subsequence matching is what lets "atndnce" find "attendance" and "chrg a
// prnt" find "charge a parent". A lower score is a better match; non-matches
// return null and are dropped.
// ---------------------------------------------------------------------------

export interface IntentMatch {
  intent: Intent;
  /** Lower is better. */
  score: number;
}

function normalize(value: string): string {
  return value.toLowerCase().trim().replace(/\s+/g, ' ');
}

/**
 * Score a single normalized haystack against a normalized query. Returns null
 * when the query is not a subsequence of the haystack. Lower is better.
 */
function scoreString(haystack: string, query: string): number | null {
  if (query.length === 0) return 0;
  if (haystack === query) return 0;
  if (haystack.startsWith(query)) return 1;

  const substringIndex = haystack.indexOf(query);
  if (substringIndex >= 0) {
    // Earlier, word-boundary-aligned substrings rank better.
    const boundaryBonus = substringIndex === 0 || haystack[substringIndex - 1] === ' ' ? 0 : 1;
    return 2 + boundaryBonus + substringIndex / 100;
  }

  // Subsequence: every query char appears in order. Penalize the total span so
  // tightly-clustered matches beat scattered ones.
  let hayIndex = 0;
  let firstMatch = -1;
  let lastMatch = -1;
  for (const char of query) {
    let found = -1;
    while (hayIndex < haystack.length) {
      if (haystack[hayIndex] === char) {
        found = hayIndex;
        hayIndex += 1;
        break;
      }
      hayIndex += 1;
    }
    if (found === -1) return null;
    if (firstMatch === -1) firstMatch = found;
    lastMatch = found;
  }
  const span = lastMatch - firstMatch;
  return 6 + span / 100;
}

/**
 * Best score for an intent against the query, considering its label and every
 * synonym. Returns null when nothing matches.
 */
export function scoreIntent(intent: Intent, rawQuery: string): number | null {
  const query = normalize(rawQuery);
  if (query.length === 0) return 0;

  const haystacks = [normalize(intent.label), ...intent.synonyms.map(normalize)];
  let best: number | null = null;
  for (const haystack of haystacks) {
    const score = scoreString(haystack, query);
    if (score !== null && (best === null || score < best)) {
      best = score;
    }
  }
  return best;
}

/**
 * Fuzzy-search the catalog for `query`, restricted to intents the `role` may
 * see. Results are sorted best-first (then by catalog stability via label) and
 * capped at `limit`. An empty query returns the role's intents in catalog
 * order (useful for a "browse everything" affordance), capped at `limit`.
 */
export function searchIntents(query: string, role: Role | null | undefined, limit = 8): Intent[] {
  const visible = intentsForRole(role);
  const normalized = normalize(query);

  if (normalized.length === 0) {
    return visible.slice(0, limit);
  }

  const matches: IntentMatch[] = [];
  for (const intent of visible) {
    const score = scoreIntent(intent, normalized);
    if (score !== null) {
      matches.push({ intent, score });
    }
  }

  matches.sort((a, b) => {
    if (a.score !== b.score) return a.score - b.score;
    return a.intent.label.localeCompare(b.intent.label);
  });

  return matches.slice(0, limit).map((m) => m.intent);
}
